import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Typography,
  IconButton,
  FormControl,
  Input,
  MenuItem,
  Select,
  useTheme,
  Theme,
  SelectChangeEvent,
} from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import { humanize } from "@/utils/string";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    flex: 1,
  },

  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },

  itemContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    width: "100%",
    marginBottom: theme.spacing(2),
  },

  item: {
    padding: theme.spacing(1, 2),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: "1px solid #EDF0F5",
    borderRadius: 50,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  itemText: {
    maxWidth: "80%",
    marginRight: theme.spacing(2),
  },

  itemDeleteButton: {
    background: "#EDF0F5",
  },

  itemDeleteButtonIcon: {
    fontSize: 16,
  },
}));

const StyledInput = withStyles(Input, (theme: Theme) => ({
  root: {
    border: `1px solid ${theme.palette.grey[200]}`,
    padding: 12,
  },
}));

function getStyles(name: string, personName: string[], theme: Theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

type Props = {
  options: string[];
  values: string[];
  onSelect: (selected: string[]) => void;
};
const MultipleSelect: React.FC<Props> = ({ options, onSelect, values }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const [selectedItems, setSelected] = React.useState<string[]>(values);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    setSelected(event.target.value as string[]);
    if (onSelect) onSelect(event.target.value as string[]);
  };

  const handleRemoveItem = (index: number) => {
    const newselectedItems = selectedItems.filter(
      (_, itemIndex) => index !== itemIndex
    );
    setSelected(newselectedItems);
    if (onSelect) onSelect(newselectedItems);
  };

  return (
    <div className={classes.root}>
      <FormControl className={classes.formControl}>
        <Select
          multiple
          value={values}
          onChange={handleChange}
          input={<StyledInput disableUnderline />}
          inputProps={{ "aria-label": "Without label" }}
          displayEmpty
          MenuProps={{
            variant: "menu",
            anchorOrigin: {
              horizontal: "center",
              vertical: "bottom",
            },
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
          renderValue={(selected) => {
            const selectedCound = (selected as string[]).length;
            return <Typography>{selectedCound} selected</Typography>;
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option}
              value={option}
              style={getStyles(option, selectedItems, theme)}
            >
              {humanize(option)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <div className={classes.itemContainer}>
        {selectedItems.map((item: string, index: number) => (
          <div key={index} className={classes.item}>
            <Typography className={classes.itemText} noWrap variant="caption">
              {humanize(item)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleRemoveItem(index)}
              className={classes.itemDeleteButton}
            >
              <CloseIcon className={classes.itemDeleteButtonIcon} />
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultipleSelect;
